<template>
    <v-container class="px-5">
        <v-row align="center" class="mb-1">
            <v-col cols="auto">
                <h4>Clean Truck Check - Submissions</h4>
                <div class="text-caption text-medium-emphasis">All companies. Click a row to inspect the capture.</div>
            </v-col>
            <v-spacer/>
            <v-col cols="auto">
                <v-btn variant="text" prepend-icon="mdi-shield-check-outline" :to="{ name: 'admin-ctc-readiness' }">
                    Readiness
                </v-btn>
            </v-col>
        </v-row>

        <v-card rounded="lg" variant="outlined" class="mb-3">
            <v-card-text>
                <v-row dense align="center">
                    <v-col cols="12" sm="4">
                        <v-text-field v-model="filters.device_serial" label="Device serial" density="compact"
                                      variant="outlined" hide-details clearable prepend-inner-icon="mdi-magnify"
                                      @update:model-value="debouncedFetch"/>
                    </v-col>
                    <v-col cols="6" sm="3">
                        <v-select v-model="filters.transport_status" :items="transportOptions" label="Transport"
                                  density="compact" variant="outlined" hide-details clearable
                                  @update:model-value="resetFetch"/>
                    </v-col>
                    <v-col cols="6" sm="3">
                        <v-select v-model="filters.outcome" :items="outcomeOptions" label="Outcome"
                                  density="compact" variant="outlined" hide-details clearable
                                  @update:model-value="resetFetch"/>
                    </v-col>
                    <v-col cols="12" sm="2">
                        <v-switch v-model="filters.failed_only" label="Failed only" color="error" density="compact"
                                  hide-details @update:model-value="resetFetch"/>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-data-table-server v-model:items-per-page="perPage" v-model:page="page"
                             :headers="headers" :items="rows" :items-length="total" :loading="loading"
                             class="rounded-lg" hover @update:options="fetch"
                             @click:row="(_, { item }) => openDetail(item)">
            <template v-slot:item.device_serial="{ item }">
                <code class="mr-1">{{ item.device_serial || '-' }}</code>
                <v-btn v-if="item.device_serial" icon size="x-small" variant="text"
                       @click.stop="copyToClipboard(item.device_serial)">
                    <v-icon size="14">mdi-content-copy</v-icon>
                </v-btn>
            </template>
            <template v-slot:item.collected_at="{ item }">{{ item.collected_at ? formatDateTime(item.collected_at) : '-' }}</template>
            <template v-slot:item.submitted_at="{ item }">{{ item.submitted_at ? formatDateTime(item.submitted_at) : '-' }}</template>
            <template v-slot:item.outcome="{ item }">
                <v-chip size="x-small" variant="tonal" :color="outcomeColor(item)">{{ outcomeLabel(item) }}</v-chip>
            </template>
            <template v-slot:item.transport_status="{ item }">
                <v-chip size="x-small" variant="tonal" :color="transportColor(item.transport_status)">
                    {{ item.transport_status || '-' }}
                </v-chip>
            </template>
        </v-data-table-server>
    </v-container>
</template>

<script setup>
import {onMounted, ref} from 'vue';
import {useRouter} from 'vue-router';
import {send} from '@/composables/apiClient';
import {useSnackbarStore} from '@/stores/snackBarStore';
import {useAuthStore} from '@/stores/authStore';
import {useClipboard} from '@/composables/clipboard';
import {formatDateTime} from '@/composables/useTimezone';

const router = useRouter();
const {showSnackbar} = useSnackbarStore();
const authStore = useAuthStore();
const {copyToClipboard} = useClipboard();

const loading = ref(false);
const rows = ref([]);
const total = ref(0);
const page = ref(1);
const perPage = ref(25);
const filters = ref({device_serial: '', transport_status: null, outcome: null, failed_only: false});
let debounceTimer = null;

const transportOptions = ['queued', 'sent', 'acked', 'failed'];
const outcomeOptions = [
    {title: 'Data', value: 'data'},
    {title: 'Failed communication', value: 'failed_communication'},
    {title: 'Vehicle not OBD compliant', value: 'vehicle_not_obd_compliant'},
];

const headers = [
    {title: 'Company', key: 'company'},
    {title: 'Device', key: 'device_serial', sortable: false},
    {title: 'Asset', key: 'asset_name'},
    {title: 'Collected', key: 'collected_at'},
    {title: 'Submitted', key: 'submitted_at'},
    {title: 'Outcome', key: 'outcome', sortable: false},
    {title: 'Transport', key: 'transport_status', sortable: false},
];

const outcomeColor = (item) => {
    if (item.outcome !== 'data') return 'error';
    if (item.carb_status === 'pass') return 'success';
    if (item.carb_status === 'fail') return 'error';
    return 'grey';
};
const outcomeLabel = (item) => (item.outcome === 'data' ? (item.carb_status || 'data') : item.outcome.replace(/_/g, ' '));
const transportColor = (s) => ({sent: 'success', acked: 'success', failed: 'error', queued: 'grey'}[s] || 'grey');

const fetch = () => {
    loading.value = true;
    send('get', 'admin/ctc/submissions', {
        page: page.value,
        device_serial: filters.value.device_serial || undefined,
        transport_status: filters.value.transport_status || undefined,
        outcome: filters.value.outcome || undefined,
        failed_only: filters.value.failed_only ? 1 : undefined,
    }).then((res) => {
        const payload = res.data.data;
        rows.value = payload.data ?? [];
        total.value = payload.meta?.total ?? 0;
    }).catch(() => showSnackbar('Submissions could not be loaded', 'error'))
        .finally(() => loading.value = false);
};

const resetFetch = () => {
    page.value = 1;
    fetch();
};

const debouncedFetch = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(resetFetch, 300);
};

const openDetail = (item) => router.push({name: 'admin-ctc-submission', params: {id: item.id}});

onMounted(() => {
    if (!authStore.user?.super) {
        showSnackbar('Super admin access required', 'error');
        router.replace({name: 'dashboard'});
    }
});
</script>

<style scoped>
code {
    font-family: monospace;
}
</style>
